import { collection, doc, setDoc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

class GeoService {
  constructor() {
    this.collectionName = 'geocoding_cache';
    this.cache = new Map();
  }

  // Parse coordinates from various formats
  parseCoords(coords) {
    if (!coords) return null;
    
    // If already an object with lat/lng
    if (typeof coords === 'object' && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
      return coords;
    }
    
    // If string format "lat,lng"
    if (typeof coords === 'string') {
      const parts = coords.split(',').map(s => parseFloat(s.trim()));
      if (parts.length === 2 && parts.every(Number.isFinite)) {
        return { lat: parts[0], lng: parts[1] };
      }
    }
    
    return null;
  }

  // Generate Google Maps URL
  generateGoogleMapsUrl(coords) {
    const parsed = this.parseCoords(coords);
    if (!parsed) return null;
    return `https://www.google.com/maps?q=${parsed.lat},${parsed.lng}&z=15`;
  }

  // Round coordinates for caching (4-5 decimal places)
  roundCoords(coords, precision = 4) {
    const factor = Math.pow(10, precision);
    return {
      lat: Math.round(coords.lat * factor) / factor,
      lng: Math.round(coords.lng * factor) / factor
    };
  }

  // Generate cache key
  getCacheKey(coords) {
    const rounded = this.roundCoords(coords);
    return `${rounded.lat},${rounded.lng}`;
  }

  // Reverse geocode using Google Geocoding API
  async reverseGeocodeGoogle(coords) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      throw new Error('Google Maps API key not configured');
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coords.lat},${coords.lng}&key=${apiKey}&language=ar`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Google Geocoding API error: ${response.status}`);
    }

    const data = await response.json();
    if (data.status !== 'OK' || !data.results.length) {
      return { city: null, district: null };
    }

    const result = data.results[0];
    let city = null;
    let district = null;

    // Extract city and district from address components
    for (const component of result.address_components) {
      const types = component.types;
      
      if (types.includes('locality') || types.includes('administrative_area_level_2')) {
        city = component.long_name;
      }
      
      if (types.includes('sublocality') || types.includes('administrative_area_level_3')) {
        district = component.long_name;
      }
    }

    return { city, district };
  }

  // Reverse geocode using Nominatim (OpenStreetMap)
  async reverseGeocodeNominatim(coords) {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&accept-language=ar&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Logistiat-FuelAnalytics/1.0'
      }
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.address) {
      return { city: null, district: null };
    }

    const address = data.address;
    const city = address.city || address.town || address.village || address.municipality || null;
    const district = address.suburb || address.neighbourhood || address.quarter || null;

    return { city, district };
  }

  // Get from cache (memory first, then Firestore)
  async getFromCache(cacheKey) {
    // Check memory cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check Firestore cache
    try {
      const docRef = doc(db, this.collectionName, cacheKey);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Store in memory cache
        this.cache.set(cacheKey, data);
        return data;
      }
    } catch (error) {
      console.warn('Error reading from geocoding cache:', error);
    }

    return null;
  }

  // Save to cache (both memory and Firestore)
  async saveToCache(cacheKey, data) {
    // Save to memory cache
    this.cache.set(cacheKey, data);

    // Save to Firestore cache
    try {
      const docRef = doc(db, this.collectionName, cacheKey);
      await setDoc(docRef, {
        ...data,
        cached_at: new Date(),
        cache_key: cacheKey
      });
    } catch (error) {
      console.warn('Error saving to geocoding cache:', error);
    }
  }

  // Reverse geocode with caching
  async reverseGeocode(coords) {
    const parsed = this.parseCoords(coords);
    if (!parsed) {
      return { city: null, district: null };
    }

    const cacheKey = this.getCacheKey(parsed);
    
    // Try cache first
    const cached = await this.getFromCache(cacheKey);
    if (cached) {
      return { city: cached.city, district: cached.district };
    }

    // Try geocoding services
    let result = { city: null, district: null };
    
    try {
      // Try Google first if API key is available
      if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        result = await this.reverseGeocodeGoogle(parsed);
      } else {
        // Fallback to Nominatim with rate limiting
        await this.rateLimitDelay();
        result = await this.reverseGeocodeNominatim(parsed);
      }
    } catch (error) {
      console.warn('Geocoding failed:', error);
      // Return null values but still cache the failure to avoid repeated attempts
      result = { city: null, district: null };
    }

    // Cache the result
    await this.saveToCache(cacheKey, result);
    
    return result;
  }

  // Rate limiting for Nominatim (1 request per second)
  async rateLimitDelay() {
    const lastRequest = this.lastNominatimRequest || 0;
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequest;
    
    if (timeSinceLastRequest < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastRequest));
    }
    
    this.lastNominatimRequest = Date.now();
  }

  // Batch reverse geocode unique coordinates
  async batchReverseGeocode(coordsList) {
    const uniqueCoords = new Map();
    const results = new Map();

    // Deduplicate coordinates
    for (const coords of coordsList) {
      const parsed = this.parseCoords(coords);
      if (parsed) {
        const cacheKey = this.getCacheKey(parsed);
        uniqueCoords.set(cacheKey, parsed);
      }
    }

    // Process unique coordinates
    for (const [cacheKey, coords] of uniqueCoords) {
      try {
        const result = await this.reverseGeocode(coords);
        results.set(cacheKey, result);
      } catch (error) {
        console.warn(`Failed to geocode ${cacheKey}:`, error);
        results.set(cacheKey, { city: null, district: null });
      }
    }

    return results;
  }
}

export const geoService = new GeoService();